/**
 * #1308 (C-5, PRD O-4): recency buckets for the history drawer —
 * Today · This week · Earlier. Pure date math, brand copy applied by the
 * caller (the keys here are structural, not rendered).
 *
 * Semantics (grammar §31.3):
 * - today: same local calendar date as `now`
 * - thisWeek: within the previous 7 days (not today) — the row meta renders
 *   a weekday name, which is only unambiguous inside a 7-day window
 * - earlier: everything else (including anything with no usable timestamp)
 * Ordering inside each bucket is preserved from the input (callers pass
 * recency-sorted lists). Empty buckets are simply absent from the result.
 */
import { chatCopy } from '../../config';

export type RecencyGroupKey = 'today' | 'thisWeek' | 'earlier';

export interface RecencyGroup<T> {
  key: RecencyGroupKey;
  items: T[];
}

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

export function recencyGroupKey(timestamp: number | undefined, now: Date = new Date()): RecencyGroupKey {
  if (!timestamp || !Number.isFinite(timestamp)) return 'earlier';
  const then = new Date(timestamp);
  if (
    then.getFullYear() === now.getFullYear() &&
    then.getMonth() === now.getMonth() &&
    then.getDate() === now.getDate()
  ) {
    return 'today';
  }
  const age = now.getTime() - timestamp;
  if (age >= 0 && age < WEEK_MS) return 'thisWeek';
  return 'earlier';
}

export function groupByRecency<T>(
  items: T[],
  getTimestamp: (item: T) => number | undefined,
  now: Date = new Date()
): Array<RecencyGroup<T>> {
  const buckets: Record<RecencyGroupKey, T[]> = { today: [], thisWeek: [], earlier: [] };
  for (const item of items) {
    buckets[recencyGroupKey(getTimestamp(item), now)].push(item);
  }
  return (['today', 'thisWeek', 'earlier'] as const)
    .filter((key) => buckets[key].length > 0)
    .map((key) => ({ key, items: buckets[key] }));
}

/**
 * Row meta per bucket (§31.3): clock time today, weekday this week, date
 * earlier — each non-today bucket keeps the clock time appended, because
 * untitled rows fall back to a date-shaped title and two same-day rows would
 * otherwise be indistinguishable (review finding, 2026-08-31).
 */
export function recencyRowMeta(
  timestamp: number | undefined,
  now: Date = new Date(),
  /** #2109 R3-B24: the copy pack’s BCP 47 tag, so the weekday and month follow the UI language. */
  locale?: string,
): string {
  if (!timestamp || !Number.isFinite(timestamp)) return '';
  const then = new Date(timestamp);
  if (Number.isNaN(then.getTime())) return '';
  // #3168: the date half goes through the copy pack's Intl slot, never a raw locale-date call.
  const formatDay = chatCopy().time.date;
  const time = then.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });
  switch (recencyGroupKey(timestamp, now)) {
    case 'today':
      return time;
    case 'thisWeek':
      return `${formatDay(then, { weekday: 'long' }, locale)} · ${time}`;
    case 'earlier':
      return `${formatDay(then, { month: 'short', day: 'numeric', year: 'numeric' }, locale)} · ${time}`;
  }
}
